import React, { useState, useEffect } from 'react';
import { Smartphone, Clock } from 'lucide-react';

// Type definitions
interface StatData {
  icon: any;
  label: string;
  value: string;
  trend?: number;
  color?: string;
  accentColor?: string;
}

interface StatItemProps {
  icon: any;
  label: string;
  value: string;
  trend?: number;
  className?: string;
  color?: string;
  accentColor?: string;
  isUpdating?: boolean;
}

const styles = `
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes countUp {
    0% { transform: translateY(20px); opacity: 0; }
    50% { transform: translateY(-5px); opacity: 1; }
    100% { transform: translateY(0); }
  }
  .stats-container {
    position: fixed;
    bottom: 2rem;
    right: 2rem;
    z-index: 50;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    animation: fadeIn 0.6s ease-out;
  }
  .stat-card {
    border-radius: 1rem;
    padding: 1.5rem;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    cursor: pointer;
    position: relative;
    overflow: hidden;
    background: linear-gradient(135deg, rgba(88, 86, 214, 0.15) 0%, rgba(33, 150, 243, 0.15) 100%);
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
    min-width: 240px;
  }
  .stat-card.navy {
    background: linear-gradient(135deg, rgba(0, 18, 115, 0.85) 0%, rgba(0, 18, 115, 0.95) 100%);
    border-color: rgba(0, 18, 115, 0.3);
    box-shadow: 0 8px 32px rgba(0, 18, 115, 0.4);
  }

  .stat-card.navy:hover {
    background: linear-gradient(135deg, rgba(0, 18, 115, 0.9) 0%, rgba(0, 30, 140, 0.95) 100%);
    box-shadow: 0 12px 40px rgba(0, 18, 115, 0.5);
    transform: translateY(-6px);
  }
  .stat-card.mint {
    background: linear-gradient(135deg, rgba(204, 255, 246, 0.85) 0%, rgba(204, 255, 246, 0.95) 100%);
    border-color: rgba(204, 255, 246, 0.3);
    box-shadow: 0 8px 32px rgba(204, 255, 246, 0.4);
  }

  .stat-card.mint:hover {
    background: linear-gradient(135deg, rgba(204, 255, 246, 0.9) 0%, rgba(180, 245, 235, 0.95) 100%);
    box-shadow: 0 12px 40px rgba(204, 255, 246, 0.5);
    transform: translateY(-6px);
  }
  .stat-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.08), transparent);
    transition: left 0.6s;
  }
  .stat-card:hover::before {
    left: 100%;
  }
  .stat-card:hover {
    transform: translateY(-4px);
    background: rgba(255, 255, 255, 0.05);
  }
  .stat-card:nth-child(1) {
    animation-delay: 0.1s;
  }
  .stat-card:nth-child(2) {
    animation-delay: 0.2s;
  }
  .stat-card:nth-child(3) {
    animation-delay: 0.3s;
  }
  .stat-card:nth-child(4) {
    animation-delay: 0.4s;
  }
  .stat-card:nth-child(5) {
    animation-delay: 0.5s;
  }
  .stat-header {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    margin-bottom: 0.8rem;
  }
  .stat-icon-wrapper {
    width: 48px;
    height: 48px;
    border-radius: 0.75rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.2);
    border: none;
    transition: all 0.3s ease;
  }
  .stat-card.navy .stat-icon-wrapper {
    background: rgba(255, 255, 255, 0.25);
  }
  .stat-card.mint .stat-icon-wrapper {
    background: rgba(255, 255, 255, 0.25);
  }
  .stat-card:hover .stat-icon-wrapper {
    transform: scale(1.1);
    background: rgba(255, 255, 255, 0.3);
  }
  .stat-label {
    color: rgb(156, 163, 175);
    font-size: 0.8rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    flex: 1;
  }
  .stat-card.mint .stat-label {
    color: #004A59;
  }
  .stat-value {
    font-size: 2.5rem;
    font-weight: 800;
    color: white;
    margin: 0;
    line-height: 1;
    margin-top: 0.5rem;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    animation: countUp 0.6s ease-out;
  }
  .stat-card.mint .stat-value {
    color: #004A59;
    text-shadow: none;
  }
  .stat-value.updating {
    animation: countUp 0.4s ease-out;
  }
`;

const StatItem: React.FC<StatItemProps> = ({ icon: Icon, label, value, className = "", color = "navy", accentColor = "#ffffff", isUpdating = false }) => {
  const iconColor = color === "mint" ? "#004A59" : accentColor;
  return (
    <div className={`stat-card ${color} ${isUpdating ? 'updating' : ''} ${className}`}>
      <div className="stat-header">
        <div className={`stat-icon-wrapper ${color}`}>
          <Icon size={24} color={iconColor} strokeWidth={2.5} />
        </div>
        <div className="stat-label">{label}</div>
      </div>
      <div className={`stat-value ${isUpdating ? 'updating' : ''}`}>{value}</div>
    </div>
  );
};

interface GlobeStatsPanelProps {
  totalTopups?: number;
  perMinute?: string;
  perSecond?: string;
  date?: string;
}

const GlobeStatsPanel: React.FC<GlobeStatsPanelProps> = ({ 
  totalTopups = 12845, 
  perMinute = "892", 
}) => {
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    // Simulate value updates every 5 seconds
    const updateTimer = setInterval(() => {
      setIsUpdating(true);
      setTimeout(() => setIsUpdating(false), 400);
    }, 5000);

    return () => {
      clearInterval(updateTimer);
    };
  }, []);

  const stats: StatData[] = [
    { 
      icon: Smartphone, 
      label: "Total Topups Today", 
      value: totalTopups.toLocaleString(), 
      color: 'navy',
      accentColor: '#ffffff'
    },
    { 
      icon: Clock, 
      label: "Per Minute", 
      value: perMinute, 
      color: 'mint',
      accentColor: '#ffffff'
    },
  ];

  return (
    <>
      <style>{styles}</style>
      <div className="stats-container">
        {stats.map((stat, i) => (
          <StatItem 
            color={stat.color} 
            accentColor={stat.accentColor}
            key={i} 
            {...stat} 
            isUpdating={isUpdating && i < 2}
          />
        ))}
      </div>
    </>
  );
};

export default GlobeStatsPanel;